import React, {useEffect, useState} from "react";
import {Routes, Route, Navigate, Link, useLocation, useNavigate} from "react-router-dom";
import {Layout, Menu, Button, Avatar, Dropdown, Space, Spin, Modal} from "antd";
import {
    UserOutlined,
    TeamOutlined,
    CalendarOutlined,
    BarChartOutlined,
    LineChartOutlined,
    ToolOutlined,
    AppstoreOutlined,
    PieChartOutlined,
    SendOutlined,
    HomeOutlined,
    CarOutlined,
    AudioOutlined,
    LoginOutlined,
    ReadOutlined,
    AimOutlined,
    SlidersOutlined,
    AccountBookOutlined,
    MoneyCollectOutlined,
    TrophyOutlined,
    DashboardOutlined,
    ScheduleOutlined,
    AuditOutlined,
} from "@ant-design/icons";
import useIdleLogout from "./hooks/useIdleLogout";
import RequireAuth from "./RequireAuth";
import RequirePermission from "./components/RequirePermission";
import CustomersPage from "./pages/daia/CustomersPage";
import CustomerDetailsPage from "./pages/daia/CustomersDetailPage";
import AgentAnalyticsPage from "./pages/agent/AgentAnalyticsPage";
import AgentChangePasswordPage from "./pages/agent/AgentChangePasswordPage";
import AgentsPage from "./pages/daia/AgentsPage";
import AgentProfilePage from "./pages/agent/AgentProfilePage";
import AgentSettlementsPage from "./pages/daia/AgentSettlementsPage";
import AgentPortalLayout from "./components/agent/AgentPortalLayout";
import AgentPortalSettlementsPage from "./pages/agent/AgentPortalSettlementsPage";
import TripsPage from "./pages/daia/TripsPage";
import TripDetailPage from "./pages/daia/TripDetailPage";
import NewTripPage from "./pages/daia/NewTripPage";
import AnalyticsPage from "./pages/daia/AnalyticsPage";
import ToolsPage from "./pages/daia/ToolsPage";
import Login from "./pages/Login";
import DolphinRatesPage from "./pages/dolphin/DolphinRatesPage";
import TransactionsPage from "./pages/dolphin/TransactionsPage";
import RegistrationsPage from "./pages/cc/RegistrationsPage";
import RegistrationDetailPage from "./pages/cc/RegistrationDetailPage";
import CCCustomersPage from "./pages/cc/CCCustomersPage";
import CCCustomerDetailPage from "./pages/cc/CCCustomerDetailPage";
import FlightsPage from "./pages/cc/FlightsPage";
import FlightDetailPage from "./pages/cc/FlightDetailPage";
import TransportationPage from "./pages/cc/TransportationPage";
import TransportationDetailPage from "./pages/cc/TransportationDetailPage";
import HotelPage from "./pages/cc/HotelPage";
import HotelDetailPage from "./pages/cc/HotelDetailPage";
import CCTripsPage from "./pages/cc/CCTripsPage";
import CCTripDetailPage from "./pages/cc/CCTripDetailPage";
import EntertainmentPage from "./pages/cc/EntertainmentPage";
import UserProfilePage from "./pages/users/UserProfilePage";
import AllUsersPage from "./pages/users/AllUsersPage";
import WorkSchedulesPage from "./pages/users/WorkSchedulesPage";
import MonthlySchedulePage from "./pages/cc/MonthlySchedulePage";
import SettlementsPage from "./pages/cc/SettlementsPage";
import TopPlayers from "./pages/cc/TopPlayers";
import DaiaCarsPage from "./pages/daia/DaiaCarsPage";
import DaiaFlightsPage from "./pages/daia/FlightsPage";
import DaiaFlightDetailPage from "./pages/daia/FlightDetailPage";
import NotFoundOrForbidden from "./pages/NotFoundOrForbidden";
import CCAnalyticsPage from "./pages/cc/CCAnalyticsPage";
import PerformancePage from "./pages/cc/PerformancePage";
import InspirePTComingSoonPage from "./pages/InspirePTComingSoonPage";
import DeviceGuard from "./components/DeviceGuard";
import TargetsPage from "./pages/daia/TargetsPage";
import OverviewPage from "./pages/ultra/OverviewPage";
import EventsPage from "./pages/ultra/EventsPage";
import EventDetailsPage from "./pages/ultra/EventDetialsPage";
import MFASetup from "./pages/MFASetup";


const {Header, Content, Sider} = Layout;
const {SubMenu} = Menu;

const handleLogout = () => {
    sessionStorage.removeItem("pending_mfa_token");
    sessionStorage.removeItem("pending_mfa_remember");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("refresh");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    window.location.href = "/login";
};

const RequireAgent = ({children}: { children: React.ReactNode }) => {
    const token = localStorage.getItem("access") || sessionStorage.getItem("access");
    const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
    );

    if (!token) {
        return <Navigate to="/login" replace/>;
    }

    if (user?.account_type !== "agent") {
        return <Navigate to="/" replace/>;
    }

    return <>{children}</>;
};

const App = () => {
    const location = useLocation();
    const [authChecked, setAuthChecked] = useState(false);
    const getToken = () => localStorage.getItem("access") || sessionStorage.getItem("access");
    const isAuthenticated = !!getToken();
    const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
    );
    const isAgentRoute = location.pathname.startsWith("/agent");
    const isAgentUser = user?.account_type === "agent";
    const isPublicAuthRoute =
        location.pathname === "/login" ||
        location.pathname === "/setup-mfa";

    const canViewPerformance = Boolean(user?.can_view_performance_dashboard);
    const navigate = useNavigate()


    const {showWarning, secondsRemaining, stayLoggedIn} = useIdleLogout({
        isAuthenticated,
        onLogout: handleLogout,
        enabled: isAuthenticated && !isAgentRoute && !isAgentUser && !isPublicAuthRoute,
    });

    useEffect(() => {
        setAuthChecked(true);

        if (!isAuthenticated && location.pathname === "/") {
            navigate("/login", {replace: true});
            return;
        }

        if (
            isAuthenticated &&
            (location.pathname === "/" || location.pathname === "/login") &&
            user?.account_type
        ) {
            if (user.account_type === "ccdaia") {
                navigate("/cc/registrations", {replace: true});
            } else if (user.account_type === "dolphin") {
                navigate("/dolphin/rates", {replace: true});
            } else if (user.account_type === "agent") {
                navigate("/agent/settlements", {replace: true});
            }
        }
    }, [isAuthenticated, location.pathname, navigate, user?.account_type]);

    if (!authChecked) {
        return (
            <div style={{height: "100vh", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Spin size="large"/>
            </div>
        );
    }

    const authContent = (
        <Routes>
            <Route
                path="/login"
                element={
                    !authChecked
                        ? <Spin size="large"/>
                        : isAuthenticated && !user?.mfa_exempt && !user?.mfa_enabled
                            ? <Navigate to="/setup-mfa" replace/>
                            : isAuthenticated && user?.account_type === "ccdaia"
                                ? <Navigate to="/cc/registrations" replace/>
                                : isAuthenticated && user?.account_type === "dolphin"
                                    ? <Navigate to="/dolphin/rates" replace/>
                                    : isAuthenticated && user?.account_type === "agent"
                                        ? <Navigate to="/agent/settlements" replace/>
                                        : <Login/>
                }
            />

            <Route
                path="/setup-mfa"
                element={
                    <RequireAuth>
                        <MFASetup/>
                    </RequireAuth>
                }
            />

            <Route path="*" element={<Navigate to="/login" replace/>}/>
        </Routes>
    );

    const agentContent = (
        <Routes>
            <Route
                path="/agent/settlements"
                element={
                    <RequireAgent>
                        <AgentPortalLayout>
                            <AgentPortalSettlementsPage/>
                        </AgentPortalLayout>
                    </RequireAgent>
                }
            />

            <Route
                path="/agent/analytics"
                element={
                    <RequireAgent>
                        <AgentPortalLayout>
                            <AgentAnalyticsPage/>
                        </AgentPortalLayout>
                    </RequireAgent>
                }
            />

            <Route
                path="/agent/profile"
                element={
                    <RequireAgent>
                        <AgentPortalLayout>
                            <AgentProfilePage/>
                        </AgentPortalLayout>
                    </RequireAgent>
                }
            />

            <Route
                path="/agent/change-password"
                element={
                    <RequireAgent>
                        <AgentPortalLayout>
                            <AgentChangePasswordPage/>
                        </AgentPortalLayout>
                    </RequireAgent>
                }
            />

            <Route path="/agent" element={<Navigate to="/agent/settlements" replace/>}/>
            <Route path="*" element={<NotFoundOrForbidden/>}/>
        </Routes>
    );

    const internalContent = (
        <Layout style={{minHeight: "100vh"}}>
            {isAuthenticated && (
                <Sider>
                    <div style={{
                        height: 64,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "16px 0"
                    }}>
                        <img src="/vertical-white.png" alt="Logo" style={{maxWidth: 140, maxHeight: 60}}/>
                    </div>

                    <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]}>
                        {isAuthenticated && user.account_type === "ccdaia" && (
                            <>
                                <SubMenu key="users" icon={<UserOutlined/>} title="Users">
                                    <Menu.Item key="/users/all" icon={<TeamOutlined/>}>
                                        <Link to="/users/all">All Users</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/users/schedules" icon={<CalendarOutlined/>}>
                                        <Link to="/users/schedules">Work Schedule</Link>
                                    </Menu.Item>
                                </SubMenu>
                                <SubMenu key="casino-club" icon={<AppstoreOutlined/>} title="Casino Club">
                                    <Menu.Item key="/cc/registrations" icon={<TeamOutlined/>}>
                                        <Link to="/cc/registrations">Registrations</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/customers" icon={<UserOutlined/>}>
                                        <Link to="/cc/customers">Customers</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/trips" icon={<ReadOutlined/>}>
                                        <Link to="/cc/trips">Trips</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/flights" icon={<SendOutlined/>}>
                                        <Link to="/cc/flights">Flights</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/transportation" icon={<CarOutlined/>}>
                                        <Link to="/cc/transportation">Transportation</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/hotel" icon={<HomeOutlined/>}>
                                        <Link to="/cc/hotel">Hotel</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/entertainment" icon={<AudioOutlined/>}>
                                        <Link to="/cc/entertainment">Entertainment</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/calendar" icon={<CalendarOutlined/>}>
                                        <Link to="/cc/calendar">Calendar </Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/settlements" icon={<MoneyCollectOutlined/>}>
                                        <Link to="/cc/settlements">Settlements </Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/top-players" icon={<TrophyOutlined/>}>
                                        <Link to="/cc/top-players">Top Players</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/cc/analytics" icon={<PieChartOutlined/>}>
                                        <Link to="/cc/analytics">Analytics</Link>
                                    </Menu.Item>
                                    {canViewPerformance && (
                                        <Menu.Item key="/cc/performance" icon={<LineChartOutlined/>}>
                                            <Link to="/cc/performance">Performance</Link>
                                        </Menu.Item>
                                    )}
                                </SubMenu>

                                <SubMenu key="daia" icon={<AppstoreOutlined/>} title="DAIA">
                                    <Menu.Item key="/daia/customers" icon={<UserOutlined/>}>
                                        <Link to="/daia/customers">Customers</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/agents" icon={<TeamOutlined/>}>
                                        <Link to="/daia/agents">Agents</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/agent-settlements" icon={<AuditOutlined/>}>
                                        <Link to="/daia/agent-settlements">Agent Settlements</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/trips" icon={<ReadOutlined/>}>
                                        <Link to="/daia/trips">Trips</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/flights" icon={<SendOutlined/>}>
                                        <Link to="/daia/flights">DAIA Flights</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/targets" icon={<AimOutlined/>}>
                                        <Link to="/daia/targets">Targets</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/cars" icon={<CarOutlined/>}>
                                        <Link to="/daia/cars">Daia Cars</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/analytics" icon={<BarChartOutlined/>}>
                                        <Link to="/daia/analytics">Analytics</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/daia/tools" icon={<ToolOutlined/>}>
                                        <Link to="/daia/tools">Tools</Link>
                                    </Menu.Item>
                                </SubMenu>

                                <SubMenu key="ultra" icon={<AppstoreOutlined/>} title="Ultra Lounge">
                                    <Menu.Item key="/ul/overview" icon={<DashboardOutlined/>}>
                                        <Link to="/ul/overview">Overview</Link>
                                    </Menu.Item>
                                    <Menu.Item key="/ul/events" icon={<ScheduleOutlined/>}>
                                        <Link to="/ul/events">Events</Link>
                                    </Menu.Item>
                                </SubMenu>
                            </>
                        )}
                        {isAuthenticated && ["ccdaia", "dolphin"].includes(user.account_type) && (
                            <SubMenu key="dolphin" icon={<AppstoreOutlined/>} title="Dolphin">
                                <Menu.Item key="/dolphin/rates" icon={<SlidersOutlined/>}>
                                    <Link to="/dolphin/rates">Rates</Link>
                                </Menu.Item>
                                <Menu.Item key="/dolphin/transactions" icon={<AccountBookOutlined/>}>
                                    <Link to="/dolphin/transactions">Transactions</Link>
                                </Menu.Item>
                            </SubMenu>
                        )}

                        {isAuthenticated && user.account_type === "ccdaia" && (
                            <Menu.Item key="/poker">
                                <Link to="/poker">Poker</Link>
                            </Menu.Item>
                        )}
                        {isAuthenticated ? (
                            <Menu.Item key="logout" icon={<LoginOutlined/>}>
                                <Button type="primary" danger block onClick={handleLogout}>
                                    Logout
                                </Button>
                            </Menu.Item>
                        ) : (
                            <Menu.Item key="/login" icon={<LoginOutlined/>}>
                                <Link to="/login">
                                    <Button type="primary" block>
                                        Login
                                    </Button>
                                </Link>
                            </Menu.Item>
                        )}
                    </Menu>
                </Sider>
            )}

            <Layout>
                {isAuthenticated && (
                    <Header
                        style={{
                            background: "#fff",
                            padding: "0 24px",
                            display: "flex",
                            justifyContent: "flex-end"
                        }}>
                        {isAuthenticated && (
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'profile',
                                            icon: <UserOutlined/>,
                                            label: <Link to="/profile">My Profile</Link>,
                                        },
                                        {
                                            key: 'logout',
                                            icon: <LoginOutlined/>,
                                            label: 'Logout',
                                        }
                                    ],
                                    onClick: ({key}) => {
                                        if (key === 'logout') {
                                            handleLogout();
                                        }
                                    }
                                }}
                                placement="bottomRight"
                            >
                                <Space style={{cursor: "pointer"}}>
                                    <Avatar style={{backgroundColor: "#1890ff"}} icon={<UserOutlined/>}/>
                                    <span style={{fontWeight: 500}}>{user?.first_name
                                        ? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1).toLowerCase()
                                        : ""}</span>
                                </Space>
                            </Dropdown>
                        )}
                    </Header>
                )}
                <Content style={{margin: "16px", padding: "20px", background: "#fff"}}>
                    <Routes>
                        {user?.account_type === "agent" && (
                            <Route path="*" element={<Navigate to="/agent/settlements" replace/>}/>
                        )}

                        {isAuthenticated && ["ccdaia", "dolphin"].includes(user?.account_type) && (
                            <Route
                                path="/profile"
                                element={
                                    <RequireAuth>
                                        <UserProfilePage/>
                                    </RequireAuth>
                                }
                            />
                        )}

                        {user?.account_type === "ccdaia" && (
                            <>
                                {/* Render ALL routes */}
                                {/* User Routes */}
                                <Route path="/users/all" element={<RequireAuth><AllUsersPage/></RequireAuth>}/>
                                <Route path="/users/schedules"
                                       element={<RequireAuth><WorkSchedulesPage/></RequireAuth>}/>

                                {/* Casino Club Routes */}
                                <Route path="/cc/registrations"
                                       element={<RequireAuth><RegistrationsPage/></RequireAuth>}/>
                                <Route path="/cc/registrations/:id"
                                       element={<RequireAuth><RegistrationDetailPage/></RequireAuth>}/>
                                <Route path="/cc/customers"
                                       element={<RequireAuth><CCCustomersPage/></RequireAuth>}/>
                                <Route path="/cc/customers/:id"
                                       element={<RequireAuth><CCCustomerDetailPage/></RequireAuth>}/>
                                <Route path="/cc/trips" element={<RequireAuth><CCTripsPage/></RequireAuth>}/>
                                <Route path="/cc/trips/:id/details"
                                       element={<RequireAuth><CCTripDetailPage/></RequireAuth>}/>
                                <Route path="/cc/flights" element={<RequireAuth><FlightsPage/></RequireAuth>}/>
                                <Route path="/cc/flights/:id"
                                       element={<RequireAuth><FlightDetailPage/></RequireAuth>}/>
                                <Route path="/cc/transportation"
                                       element={<RequireAuth><TransportationPage/></RequireAuth>}/>
                                <Route path="/cc/transportation/:id"
                                       element={<RequireAuth><TransportationDetailPage/></RequireAuth>}/>
                                <Route path="/cc/hotel" element={<RequireAuth><HotelPage/></RequireAuth>}/>
                                <Route path="/cc/hotel/:id"
                                       element={<RequireAuth><HotelDetailPage/></RequireAuth>}/>
                                <Route path="/cc/entertainment"
                                       element={<RequireAuth><EntertainmentPage/></RequireAuth>}/>
                                <Route path="/cc/calendar"
                                       element={<RequireAuth><MonthlySchedulePage/></RequireAuth>}/>
                                <Route
                                    path="/cc/settlements"
                                    element={<RequireAuth><SettlementsPage/></RequireAuth>}
                                />
                                <Route
                                    path="/cc/top-players"
                                    element={<RequireAuth><TopPlayers/></RequireAuth>}
                                />
                                <Route path="/cc/analytics"
                                       element={<RequireAuth><CCAnalyticsPage/></RequireAuth>}/>
                                <Route
                                    path="/cc/performance"
                                    element={
                                        <RequireAuth>
                                            <RequirePermission allowed={canViewPerformance}>
                                                <PerformancePage/>
                                            </RequirePermission>
                                        </RequireAuth>
                                    }
                                />

                                {/* DAIA Routes */}
                                <Route path="/daia/customers"
                                       element={<RequireAuth><CustomersPage/></RequireAuth>}/>
                                <Route path="/daia/customers/:id"
                                       element={<RequireAuth><CustomerDetailsPage/></RequireAuth>}/>
                                <Route path="/daia/flights" element={<RequireAuth><DaiaFlightsPage/></RequireAuth>}/>
                                <Route path="/daia/flights/:id"
                                       element={<RequireAuth><DaiaFlightDetailPage/></RequireAuth>}/>
                                <Route path="/daia/agents" element={<RequireAuth><AgentsPage/></RequireAuth>}/>
                                <Route path="/daia/agent-settlements"
                                       element={<RequireAuth><AgentSettlementsPage/></RequireAuth>}/>
                                <Route path="/daia/trips" element={<RequireAuth><TripsPage/></RequireAuth>}/>
                                <Route path="/daia/trips/new" element={<RequireAuth><NewTripPage/></RequireAuth>}/>
                                <Route path="/daia/trips/:id"
                                       element={<RequireAuth><TripDetailPage/></RequireAuth>}/>
                                <Route path="/daia/targets" element={<RequireAuth><TargetsPage/></RequireAuth>}/>
                                <Route path="/daia/cars" element={<RequireAuth><DaiaCarsPage/></RequireAuth>}/>
                                <Route path="/daia/analytics"
                                       element={<RequireAuth><AnalyticsPage/></RequireAuth>}/>
                                <Route path="/daia/tools" element={<RequireAuth><ToolsPage/></RequireAuth>}/>

                                {/* Placeholder Pages */}
                                <Route path="/poker" element={<RequireAuth>
                                    <InspirePTComingSoonPage/>
                                </RequireAuth>}/>

                                {/* Placeholder Pages */}
                                <Route path="/ul/overview" element={<RequireAuth>
                                    <OverviewPage/>
                                </RequireAuth>}/>
                                <Route path="/ul/events" element={<RequireAuth>
                                    <EventsPage/>
                                </RequireAuth>}/>
                                <Route path="/ul/events/:id" element={<RequireAuth>
                                    <EventDetailsPage/>
                                </RequireAuth>}/>

                                {/* Dolphin Routes */}
                                <Route path="/dolphin/rates"
                                       element={<RequireAuth><DolphinRatesPage/></RequireAuth>}/>
                                <Route path="/dolphin/transactions"
                                       element={<RequireAuth><TransactionsPage/></RequireAuth>}/>
                            </>
                        )}
                        {user?.account_type === "dolphin" && (
                            <>
                                <Route path="/" element={<Navigate to="/dolphin/rates" replace/>}/>
                                <Route path="/dolphin/rates"
                                       element={<RequireAuth><DolphinRatesPage/></RequireAuth>}/>
                                <Route path="/dolphin/transactions"
                                       element={<RequireAuth><TransactionsPage/></RequireAuth>}/>
                            </>
                        )}

                        <Route path="*" element={<NotFoundOrForbidden/>}/>

                    </Routes>
                </Content>
                <Modal
                    open={showWarning}
                    title="Session expiring soon"
                    closable={false}
                    maskClosable={false}
                    footer={[
                        <Button key="stay-logged-in" type="primary" onClick={stayLoggedIn}>
                            Stay logged in
                        </Button>,
                    ]}
                >
                    <p>
                        You will be logged out due to inactivity in {secondsRemaining} second
                        {secondsRemaining !== 1 ? "s" : ""}.
                    </p>
                </Modal>
            </Layout>
        </Layout>
    );

    if (isAgentRoute) {
        return agentContent;
    }

    if (isPublicAuthRoute) {
        return authContent;
    }

    return (
        <DeviceGuard>
            {internalContent}
        </DeviceGuard>
    );
};

export default App;
